import type { ProductStatus, ProductWithStock } from './types';

/**
 * Pagination, search, sort and status filtering for the product list —
 * ENTIRELY CLIENT-SIDE, on purpose.
 *
 * `GET /api/v1/merchant/products` (product.controller.ts) takes no query
 * parameters at all: it returns the tenant's full product list in one
 * response, unpaginated, unsearchable, unsortable server-side (see
 * ProductRepository.list()'s single, parameter-free `SELECT * FROM
 * products ... ORDER BY name, created_at`). Rather than inventing query
 * parameters the server does not implement, this module operates on the
 * array `useProductsQuery()` already fetched — real data, computed
 * differently, never a second network call and never fabricated rows.
 *
 * This trade-off is honest for the scale a single-tenant catalogue
 * realistically has today (tens to low hundreds of products); it would
 * need revisiting — server-side pagination — if a shop's catalogue grew
 * large enough that fetching the whole list became the bottleneck. That
 * is a real, load-bearing limit, not a stopgap this file hides.
 */

export type ProductStatusFilter = ProductStatus | 'all';

export type ProductSortKey = 'name' | 'price' | 'status' | 'stock' | 'updatedAt';
export type SortDirection = 'asc' | 'desc';

export interface ProductTableState {
  readonly search: string;
  readonly status: ProductStatusFilter;
  readonly sortKey: ProductSortKey;
  readonly sortDirection: SortDirection;
  readonly page: number;
  readonly pageSize: number;
}

export const DEFAULT_PRODUCT_TABLE_STATE: ProductTableState = {
  search: '',
  status: 'all',
  sortKey: 'updatedAt',
  sortDirection: 'desc',
  page: 1,
  pageSize: 20,
};

export function filterByStatus(
  products: readonly ProductWithStock[],
  status: ProductStatusFilter,
): readonly ProductWithStock[] {
  if (status === 'all') return products;
  return products.filter((product) => product.status === status);
}

/** Matches on name or SKU, case-insensitive, diacritics-insensitive (accented product names). */
export function searchProducts(
  products: readonly ProductWithStock[],
  query: string,
): readonly ProductWithStock[] {
  const normalised = normalise(query.trim());
  if (normalised === '') return products;

  return products.filter(
    (product) =>
      normalise(product.name).includes(normalised) || normalise(product.sku).includes(normalised),
  );
}

/** Lowercases and strips combining diacritical marks (U+0300–U+036F) so "Téléphone" matches "telephone". */
function normalise(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(DIACRITICS_PATTERN, '');
}

const DIACRITICS_PATTERN = /[̀-ͯ]/g;

const STOCK_RANK: Record<ProductWithStock['stock']['state'], number> = {
  out_of_stock: 0,
  temporarily_unavailable: 1,
  low_stock: 2,
  in_stock: 3,
};

export function sortProducts(
  products: readonly ProductWithStock[],
  sortKey: ProductSortKey,
  direction: SortDirection,
): readonly ProductWithStock[] {
  const sorted = [...products].sort((a, b) => {
    switch (sortKey) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price':
        return a.price - b.price;
      case 'status':
        return a.status.localeCompare(b.status);
      case 'stock':
        return STOCK_RANK[a.stock.state] - STOCK_RANK[b.stock.state];
      case 'updatedAt':
        return a.updatedAt.localeCompare(b.updatedAt);
      default:
        return 0;
    }
  });

  return direction === 'asc' ? sorted : sorted.reverse();
}

export interface PaginatedResult<T> {
  readonly items: readonly T[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

export function paginate<T>(
  items: readonly T[],
  page: number,
  pageSize: number,
): PaginatedResult<T> {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(Math.max(1, page), totalPages);
  const start = (clampedPage - 1) * pageSize;

  return {
    items: items.slice(start, start + pageSize),
    page: clampedPage,
    pageSize,
    total,
    totalPages,
  };
}

/** Applies status filter, search, sort and pagination, in that order — the single entry point ProductListPage calls. */
export function applyProductTableState(
  products: readonly ProductWithStock[],
  state: ProductTableState,
): PaginatedResult<ProductWithStock> {
  const filtered = filterByStatus(products, state.status);
  const searched = searchProducts(filtered, state.search);
  const sorted = sortProducts(searched, state.sortKey, state.sortDirection);
  return paginate(sorted, state.page, state.pageSize);
}
